import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { getMySchedule, getWeekSchedule } from "../../services/ScheduleService";
import { searchEmployees } from "../../services/EmployeeService";
import ShiftModal from "./ShiftModal";
import Spinner from "../CommonComponents/Spinner";
import { ROLES } from "../../services/Constants";

// --- Styled components ---

const PageWrapper = styled.div`
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1rem;
`;

const PageTitle = styled.h2`
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
`;

const WeekNav = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    padding: 0.3rem 0.5rem;
    box-shadow: 0 1px 4px ${({ theme }) => theme.colors.shadow};
`;

const NavBtn = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.2rem;
    padding: 0.25rem 0.55rem;
    cursor: pointer;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    line-height: 1;
    &:hover {
        background: ${({ theme }) => theme.colors.primary}22;
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const WeekLabel = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    min-width: 190px;
    text-align: center;
`;

const TodayBtn = styled.button`
    background: ${({ theme }) => theme.colors.primary};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.4rem 1rem;
    cursor: pointer;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    &:hover { opacity: 0.85; }
`;

const SearchInput = styled.input`
    margin-left: auto;
    padding: 0.4rem 0.8rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.875rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    min-width: 200px;
    &::placeholder { color: ${({ theme }) => theme.colors.lineColor}; }
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
    }
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: 0 2px 12px ${({ theme }) => theme.colors.shadow};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: ${({ theme }) => theme.colors.background};
    min-width: 760px;
`;

const Th = styled.th`
    padding: 0.75rem 0.5rem;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.6;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    background: ${({ theme }) => theme.colors.background};
    white-space: nowrap;
    &:first-child {
        text-align: left;
        padding-left: 1rem;
        min-width: 140px;
    }
    &:last-child {
        min-width: 70px;
        opacity: 0.8;
    }
`;

const ThDay = styled(Th)`
    opacity: 1;
    background: ${({ isToday, theme }) =>
        isToday ? theme.colors.primary + "14" : theme.colors.background};
    border-bottom: 2px solid ${({ isToday, theme }) =>
        isToday ? theme.colors.primary : theme.colors.lineColor};
    transition: background 0.2s;
`;

const DayNumber = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    font-weight: 700;
    font-size: 0.88rem;
    background: ${({ isToday, theme }) => isToday ? theme.colors.primary : "transparent"};
    color: ${({ isToday, theme }) => isToday ? "#fff" : theme.colors.text};
    margin: 0 auto;
`;

const DayName = styled.div`
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ isToday, theme }) => isToday ? theme.colors.primary : theme.colors.text};
    opacity: ${({ isToday }) => isToday ? 1 : 0.55};
    margin-bottom: 4px;
`;

const Td = styled.td`
    padding: 0.45rem 0.4rem;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lineColor};
    vertical-align: middle;
    background: ${({ isToday, theme }) =>
        isToday ? theme.colors.primary + "08" : "transparent"};
    &:first-child {
        text-align: left;
        padding-left: 1rem;
        font-weight: 600;
        font-size: 0.88rem;
        color: ${({ theme }) => theme.colors.text};
        white-space: nowrap;
        background: transparent;
    }
    &:last-child {
        font-weight: 700;
        font-size: 0.82rem;
        color: ${({ theme }) => theme.colors.primary};
        background: transparent;
    }
`;

const ShiftBadge = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.3rem 0.55rem;
    border-radius: 8px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: ${({ clickable }) => clickable ? "pointer" : "default"};
    min-width: 72px;
    transition: background 0.15s, transform 0.1s;
    ${({ hasShift, isPast, theme }) => hasShift ? `
        background: ${theme.colors.primary}1a;
        color: ${theme.colors.primary};
        border: none;
        opacity: ${isPast ? 0.55 : 1};
        &:hover { background: ${theme.colors.primary}2e; transform: scale(1.03); }
    ` : `
        background: transparent;
        color: ${theme.colors.lineColor};
        border: 1.5px dashed ${theme.colors.lineColor}66;
        opacity: ${isPast ? 0.4 : 0.75};
        &:hover { border-color: ${theme.colors.primary}88; color: ${theme.colors.primary}; }
    `}
`;

const ShiftTime = styled.span`
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
`;

const ShiftDuration = styled.span`
    font-size: 0.68rem;
    font-weight: 500;
    opacity: 0.7;
    margin-top: 1px;
`;

const WeekSummary = styled.div`
    margin-top: 1.25rem;
    padding: 0.75rem 1.25rem;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    background: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    box-shadow: 0 1px 4px ${({ theme }) => theme.colors.shadow};
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const EmptyMsg = styled.p`
    text-align: center;
    padding: 2rem;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.5;
`;

// --- Helpers ---

const DAYS_HR = ["Pon", "Uto", "Sri", "Čet", "Pet", "Sub", "Ned"];

function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

function toISODate(date) {
    return date.toISOString().slice(0, 10);
}

function formatWeekLabel(monday) {
    const sunday = addDays(monday, 6);
    const fmt = (d) => d.toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric" });
    return `${fmt(monday)} – ${fmt(sunday)}`;
}

function formatTime(timeStr) {
    if (!timeStr) return "";
    return timeStr.slice(0, 5);
}

function calcHours(startTime, endTime) {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    return mins > 0 ? mins / 60 : 0;
}

// --- Component ---

function ScheduleCalendar({ user }) {
    const isAdmin = user?.role === ROLES.ADMIN;

    const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
    const [scheduleData, setScheduleData] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null);
    const [employeeFilter, setEmployeeFilter] = useState("");

    const weekDates = Array.from({ length: 7 }, (_, i) => toISODate(addDays(weekStart, i)));
    const todayStr = toISODate(new Date());

    useEffect(() => {
        if (!isAdmin) return;
        searchEmployees("", 0, 1000)
            .then(data => {
                setAllEmployees(data.content.map(e => ({ id: e.id, name: `${e.name} ${e.surname}` })));
            })
            .catch(() => {});
    }, [isAdmin]);

    const fetchSchedule = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const start = toISODate(weekStart);
            const end = toISODate(addDays(weekStart, 6));
            if (isAdmin) {
                const data = await getWeekSchedule(start, end);
                setScheduleData(data);
            } else {
                const data = await getMySchedule(start, end);
                setScheduleData(data);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [weekStart, isAdmin]);

    useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

    const shiftMap = {};
    scheduleData.forEach((s) => {
        if (!shiftMap[s.employeeId]) shiftMap[s.employeeId] = {};
        shiftMap[s.employeeId][s.date] = s;
    });

    const allRows = isAdmin
        ? allEmployees
        : (() => {
            const employeeMap = {};
            scheduleData.forEach((s) => {
                if (!employeeMap[s.employeeId]) employeeMap[s.employeeId] = s.employeeName;
            });
            return Object.entries(employeeMap).map(([id, name]) => ({ id: Number(id), name }));
        })();

    const employees = isAdmin && employeeFilter.trim()
        ? allRows.filter(e => e.name.toLowerCase().includes(employeeFilter.toLowerCase()))
        : allRows;

    const handleCellClick = (date, employeeId, employeeName, existingShift) => {
        if (!isAdmin) return;
        setModal({ date, employeeId, employeeName, existingShift });
    };

    const handleSaved = () => {
        setModal(null);
        fetchSchedule();
    };

    // Total hours for the week across all (filtered) employees
    const totalWeekHours = employees.reduce((sum, { id }) => {
        return sum + weekDates.reduce((daySum, date) => {
            const s = shiftMap[id]?.[date];
            return daySum + (s ? calcHours(s.startTime, s.endTime) : 0);
        }, 0);
    }, 0);

    return (
        <PageWrapper>
            <PageTitle>Raspored zaposlenika</PageTitle>

            <Toolbar>
                <WeekNav>
                    <NavBtn onClick={() => setWeekStart(w => addDays(w, -7))}>&#8249;</NavBtn>
                    <WeekLabel>{formatWeekLabel(weekStart)}</WeekLabel>
                    <NavBtn onClick={() => setWeekStart(w => addDays(w, 7))}>&#8250;</NavBtn>
                </WeekNav>
                <TodayBtn onClick={() => setWeekStart(getMonday(new Date()))}>Danas</TodayBtn>
                {isAdmin && (
                    <SearchInput
                        placeholder="Pretraži zaposlenika..."
                        value={employeeFilter}
                        onChange={e => setEmployeeFilter(e.target.value)}
                    />
                )}
            </Toolbar>

            {loading ? (
                <Spinner />
            ) : error ? (
                <EmptyMsg style={{ color: "red" }}>{error}</EmptyMsg>
            ) : (
                <>
                    <TableWrapper>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Zaposlenik</Th>
                                    {weekDates.map((date, i) => {
                                        const isToday = date === todayStr;
                                        const day = date.slice(8);
                                        const month = date.slice(5, 7);
                                        return (
                                            <ThDay key={date} isToday={isToday}>
                                                <DayName isToday={isToday}>{DAYS_HR[i]}</DayName>
                                                <DayNumber isToday={isToday}>{day}</DayNumber>
                                                <div style={{ fontSize: "0.68rem", opacity: 0.55, marginTop: "2px" }}>
                                                    {month}.
                                                </div>
                                            </ThDay>
                                        );
                                    })}
                                    <Th>Σ sati</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.length === 0 ? (
                                    <tr>
                                        <Td colSpan={9}>
                                            <EmptyMsg>
                                                {isAdmin
                                                    ? "Nema zaposlenika za prikaz."
                                                    : "Nemaš smjena ovaj tjedan."}
                                            </EmptyMsg>
                                        </Td>
                                    </tr>
                                ) : (
                                    employees.map(({ id, name }) => {
                                        const rowHours = weekDates.reduce((sum, date) => {
                                            const s = shiftMap[id]?.[date];
                                            return sum + (s ? calcHours(s.startTime, s.endTime) : 0);
                                        }, 0);
                                        return (
                                            <tr key={id}>
                                                <Td>{name}</Td>
                                                {weekDates.map((date) => {
                                                    const shift = shiftMap[id]?.[date];
                                                    const isPast = date < todayStr;
                                                    const isToday = date === todayStr;
                                                    const hours = shift ? calcHours(shift.startTime, shift.endTime) : 0;
                                                    return (
                                                        <Td key={date} isToday={isToday}>
                                                            <ShiftBadge
                                                                hasShift={!!shift}
                                                                isPast={isPast}
                                                                clickable={isAdmin}
                                                                onClick={() => handleCellClick(date, id, name, shift || null)}
                                                                title={shift?.note || (isAdmin ? "Klikni za dodavanje smjene" : "")}
                                                            >
                                                                {shift ? (
                                                                    <>
                                                                        <ShiftTime>
                                                                            {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
                                                                        </ShiftTime>
                                                                        <ShiftDuration>{hours % 1 === 0 ? hours : hours.toFixed(1)}h</ShiftDuration>
                                                                    </>
                                                                ) : (
                                                                    isAdmin ? "+" : "–"
                                                                )}
                                                            </ShiftBadge>
                                                        </Td>
                                                    );
                                                })}
                                                <Td>
                                                    {rowHours > 0
                                                        ? `${rowHours % 1 === 0 ? rowHours : rowHours.toFixed(1)}h`
                                                        : "–"}
                                                </Td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </TableWrapper>

                    {employees.length > 0 && (
                        <WeekSummary>
                            <strong>Ukupno sati (tjedan):</strong>
                            <span style={{ marginLeft: "0.25rem" }}>
                                {totalWeekHours % 1 === 0 ? totalWeekHours : totalWeekHours.toFixed(1)}h
                            </span>
                            {isAdmin && employeeFilter && (
                                <span style={{ marginLeft: "0.5rem", opacity: 0.6 }}>
                                    · filtriran prikaz ({employees.length} zaposlenika)
                                </span>
                            )}
                        </WeekSummary>
                    )}
                </>
            )}

            {modal && (
                <ShiftModal
                    date={modal.date}
                    employeeId={modal.employeeId}
                    employeeName={modal.employeeName}
                    existingShift={modal.existingShift}
                    onClose={() => setModal(null)}
                    onSaved={handleSaved}
                />
            )}
        </PageWrapper>
    );
}

export default ScheduleCalendar;
